import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, LogOut, Search, Shield, ShieldCheck, UserPlus, Users,
  Bell, Flag, Megaphone, Settings, Smartphone, AlertTriangle, 
  Send, Plus, Trash2, Pencil, ToggleLeft, ToggleRight, Info,
  Wrench, RefreshCw, X, Hash, Lock
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', display_name: '', system_role: 'member' });
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('users');

  // App Control state
  const [maintenance, setMaintenance] = useState({ enabled: false, message: '' });
  const [flags, setFlags] = useState([]);
  const [banners, setBanners] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [devices, setDevices] = useState([]);
  const [appSettings, setAppSettings] = useState([]);
  const [versionPolicies, setVersionPolicies] = useState([]);

  // Channels & Teams state
  const [adminChannels, setAdminChannels] = useState([]);
  const [adminTeams, setAdminTeams] = useState([]);

  // Dialog states
  const [showFlagDialog, setShowFlagDialog] = useState(false);
  const [showBannerDialog, setShowBannerDialog] = useState(false);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [showNotifDialog, setShowNotifDialog] = useState(false);
  const [showSettingDialog, setShowSettingDialog] = useState(false);
  const [showVersionDialog, setShowVersionDialog] = useState(false);

  // Form state
  const [flagForm, setFlagForm] = useState({ key: '', label: '', enabled: true });
  const [bannerForm, setBannerForm] = useState({ title: '', message: '', level: 'info', active: true });
  const [announcementForm, setAnnouncementForm] = useState({ title: '', body: '', active: true });
  const [notifForm, setNotifForm] = useState({ target: 'all', title: '', body: '' });
  const [settingForm, setSettingForm] = useState({ key: '', value: '' });
  const [versionForm, setVersionForm] = useState({ platform: 'android', min_version: '1.0.0', force_update: false, store_url: '' });

  useEffect(() => {
    const token = sessionStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axios.get(`${API}/admin/verify`)
      .then(res => { setAdminUser(res.data.user); setLoading(false); })
      .catch(() => {
        sessionStorage.removeItem('admin_token');
        sessionStorage.removeItem('admin_user');
        toast.error('Admin session expired. Please sign in again.');
        navigate('/admin/login');
      });
  }, [navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      const url = search ? `${API}/admin/users?search=${encodeURIComponent(search)}` : `${API}/admin/users`;
      const res = await axios.get(url);
      setUsers(res.data.users);
    } catch (e) {
      if (e.response?.status === 403) { toast.error('Admin session expired'); handleLogout(); }
    }
  }, [search]);

  const fetchAppControl = useCallback(async () => {
    try {
      const [mRes, fRes, bRes, aRes, nRes, dRes, sRes, vRes] = await Promise.all([
        axios.get(`${API}/admin/app/maintenance`),
        axios.get(`${API}/admin/app/flags`),
        axios.get(`${API}/admin/app/banners`),
        axios.get(`${API}/admin/app/announcements`),
        axios.get(`${API}/admin/app/notifications`),
        axios.get(`${API}/admin/app/devices`),
        axios.get(`${API}/admin/app/settings`),
        axios.get(`${API}/admin/app/version-policy`),
      ]);
      setMaintenance(mRes.data);
      setFlags(fRes.data.flags || []);
      setBanners(bRes.data.banners || []);
      setAnnouncements(aRes.data.announcements || []);
      setNotifications(nRes.data.notifications || []);
      setDevices(dRes.data.devices || []);
      setAppSettings(sRes.data.settings || []);
      setVersionPolicies(vRes.data.policies || []);
    } catch (e) {
      console.error('Failed to fetch app control data', e);
    }
  }, []);

  const fetchChannelsAndTeams = useCallback(async () => {
    try {
      const [chRes, tRes] = await Promise.all([
        axios.get(`${API}/admin/channels`),
        axios.get(`${API}/admin/teams`),
      ]);
      setAdminChannels(chRes.data.channels || []);
      setAdminTeams(tRes.data.teams || []);
    } catch (e) {
      console.error('Failed to fetch channels/teams', e);
    }
  }, []);

  useEffect(() => { if (adminUser) fetchUsers(); }, [adminUser, fetchUsers]);
  useEffect(() => { if (adminUser && activeTab === 'app-control') fetchAppControl(); }, [adminUser, activeTab, fetchAppControl]);
  useEffect(() => { if (adminUser && activeTab === 'channels') fetchChannelsAndTeams(); }, [adminUser, activeTab, fetchChannelsAndTeams]);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/admin/login');
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { system_role: newRole });
      toast.success(`Role updated to ${newRole}`);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update role'); }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Permanently delete ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(`User ${email} deleted`);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete user'); }
  };

  const handleStatusToggle = async (userId, currentActive) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, { is_active: !currentActive });
      toast.success(`User ${!currentActive ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update status'); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users`, newUser);
      toast.success('User created successfully');
      setShowCreateDialog(false);
      setNewUser({ username: '', email: '', password: '', display_name: '', system_role: 'member' });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create user'); }
    finally { setCreating(false); }
  };

  // App Control handlers
  const toggleMaintenance = async () => {
    try {
      await axios.post(`${API}/admin/app/maintenance`, { enabled: !maintenance.enabled, message: maintenance.message });
      toast.success(`Maintenance mode ${!maintenance.enabled ? 'enabled' : 'disabled'}`);
      fetchAppControl();
    } catch (e) { toast.error('Failed to toggle maintenance'); }
  };

  const saveMaintMessage = async () => {
    try {
      await axios.post(`${API}/admin/app/maintenance`, { enabled: maintenance.enabled, message: maintenance.message });
      toast.success('Maintenance message updated');
    } catch (e) { toast.error('Failed to update message'); }
  };

  const createFlag = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/app/flags`, flagForm);
      toast.success('Feature flag created');
      setShowFlagDialog(false);
      setFlagForm({ key: '', label: '', enabled: true });
      fetchAppControl();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create flag'); }
  };

  const toggleFlag = async (key, currentEnabled) => {
    try {
      await axios.put(`${API}/admin/app/flags/${key}`, { enabled: !currentEnabled });
      toast.success(`Flag ${key} ${!currentEnabled ? 'enabled' : 'disabled'}`);
      fetchAppControl();
    } catch (e) { toast.error('Failed to toggle flag'); }
  };

  const deleteFlag = async (key) => {
    try {
      await axios.delete(`${API}/admin/app/flags/${key}`);
      toast.success('Flag deleted');
      fetchAppControl();
    } catch (e) { toast.error('Failed to delete flag'); }
  };

  const createBanner = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/app/banners`, bannerForm);
      toast.success('Banner created');
      setShowBannerDialog(false);
      setBannerForm({ title: '', message: '', level: 'info', active: true });
      fetchAppControl();
    } catch (e) { toast.error('Failed to create banner'); }
  };

  const toggleBanner = async (id, currentActive) => {
    try {
      await axios.put(`${API}/admin/app/banners/${id}`, { active: !currentActive });
      toast.success(`Banner ${!currentActive ? 'activated' : 'deactivated'}`);
      fetchAppControl();
    } catch (e) { toast.error('Failed'); }
  };

  const deleteBanner = async (id) => {
    try { await axios.delete(`${API}/admin/app/banners/${id}`); toast.success('Banner deleted'); fetchAppControl(); }
    catch (e) { toast.error('Failed'); }
  };

  const createAnnouncement = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/app/announcements`, announcementForm);
      toast.success('Announcement created');
      setShowAnnouncementDialog(false);
      setAnnouncementForm({ title: '', body: '', active: true });
      fetchAppControl();
    } catch (e) { toast.error('Failed'); }
  };

  const toggleAnnouncement = async (id, currentActive) => {
    try { await axios.put(`${API}/admin/app/announcements/${id}`, { active: !currentActive }); fetchAppControl(); }
    catch (e) { toast.error('Failed'); }
  };

  const deleteAnnouncement = async (id) => {
    try { await axios.delete(`${API}/admin/app/announcements/${id}`); toast.success('Deleted'); fetchAppControl(); }
    catch (e) { toast.error('Failed'); }
  };

  const sendNotification = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/app/notifications/send`, notifForm);
      toast.success(res.data.message);
      setShowNotifDialog(false);
      setNotifForm({ target: 'all', title: '', body: '' });
      fetchAppControl();
    } catch (e) { toast.error('Failed to send'); }
  };

  const saveSetting = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/app/settings`, settingForm);
      toast.success('Setting saved');
      setShowSettingDialog(false);
      setSettingForm({ key: '', value: '' });
      fetchAppControl();
    } catch (e) { toast.error('Failed'); }
  };

  const deleteSetting = async (key) => {
    try { await axios.delete(`${API}/admin/app/settings/${key}`); toast.success('Setting deleted'); fetchAppControl(); }
    catch (e) { toast.error('Failed'); }
  };

  const handleDeleteChannel = async (channelId, channelName) => {
    if (!window.confirm(`Permanently delete #${channelName} and all its messages? This cannot be undone.`)) return;
    try {
      const res = await axios.delete(`${API}/admin/channels/${channelId}`);
      toast.success(res.data.message);
      fetchChannelsAndTeams();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete channel'); }
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Permanently delete team "${teamName}" and ALL its channels/messages? This cannot be undone.`)) return;
    try {
      const res = await axios.delete(`${API}/admin/teams/${teamId}`);
      toast.success(res.data.message);
      fetchChannelsAndTeams();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete team'); }
  };

  const saveVersionPolicy = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/app/version-policy`, versionForm);
      toast.success('Version policy saved');
      setShowVersionDialog(false);
      setVersionForm({ platform: 'android', min_version: '1.0.0', force_update: false, store_url: '' });
      fetchAppControl();
    } catch (e) { toast.error('Failed'); }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-3 border-[hsl(var(--primary))] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  const levelColors = {
    info: 'bg-blue-100 text-blue-800 border-blue-200',
    warning: 'bg-amber-100 text-amber-800 border-amber-200',
    critical: 'bg-red-100 text-red-800 border-red-200',
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-panel">
      {/* Header */}
      <div className="sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[hsl(var(--primary))] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{fontFamily: 'Space Grotesk'}}>Admin Panel</h1>
              <p className="text-xs text-muted-foreground">Signed in as {adminUser?.display_name || adminUser?.username}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-button">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6" data-testid="admin-tabs">
            <TabsTrigger value="users" className="gap-1.5" data-testid="admin-tab-users"><Users className="w-4 h-4" /> Users</TabsTrigger>
            <TabsTrigger value="channels" className="gap-1.5" data-testid="admin-tab-channels"><Hash className="w-4 h-4" /> Channels</TabsTrigger>
            <TabsTrigger value="app-control" className="gap-1.5" data-testid="admin-tab-app-control"><Smartphone className="w-4 h-4" /> App Control</TabsTrigger>
          </TabsList>

          {/* ================== USERS TAB ================== */}
          <TabsContent value="users">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <Card><CardContent className="pt-4 pb-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary)/0.10)] flex items-center justify-center"><Users className="w-5 h-5 text-[hsl(var(--primary))]" /></div>
                <div><p className="text-2xl font-bold" style={{fontFamily: 'Space Grotesk'}}>{users.length}</p><p className="text-xs text-muted-foreground">Total Users</p></div>
              </CardContent></Card>
              <Card><CardContent className="pt-4 pb-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary)/0.10)] flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-[hsl(var(--primary))]" /></div>
                <div><p className="text-2xl font-bold" style={{fontFamily: 'Space Grotesk'}}>{users.filter(u => u.system_role === 'admin').length}</p><p className="text-xs text-muted-foreground">Admins</p></div>
              </CardContent></Card>
              <Card><CardContent className="pt-4 pb-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[hsl(152,62%,38%,0.10)] flex items-center justify-center"><div className="w-3 h-3 rounded-full bg-[hsl(var(--presence-online))]" /></div>
                <div><p className="text-2xl font-bold" style={{fontFamily: 'Space Grotesk'}}>{users.filter(u => u.is_online).length}</p><p className="text-xs text-muted-foreground">Online Now</p></div>
              </CardContent></Card>
            </div>

            <div className="flex gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search users by name, email, or username..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="admin-search-input" />
              </div>
              <Button onClick={() => setShowCreateDialog(true)} data-testid="admin-create-user-button"><UserPlus className="w-4 h-4 mr-2" /> Create User</Button>
            </div>

            <Card>
              <ScrollArea className="max-h-[60vh]">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>User</TableHead><TableHead>Email</TableHead><TableHead>Department</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2.5">
                            <div className="relative">
                              <div className="w-8 h-8 rounded-full bg-[hsl(var(--surface-3))] flex items-center justify-center text-xs font-semibold">{u.display_name?.[0] || u.username?.[0] || '?'}</div>
                              {u.is_online && <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[hsl(var(--presence-online))] border-2 border-card" />}
                            </div>
                            <div><p className="text-sm font-medium">{u.display_name || u.username}</p><p className="text-xs text-muted-foreground">@{u.username}</p></div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{u.department || '\u2014'}</TableCell>
                        <TableCell>
                          <Select value={u.system_role || 'member'} onValueChange={(val) => handleRoleChange(u.user_id, val)} disabled={u.user_id === adminUser?.user_id}>
                            <SelectTrigger className="w-28 h-8 text-xs" data-testid={`admin-role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="member">Member</SelectItem></SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.is_active !== false ? 'default' : 'secondary'} className={u.is_active !== false ? 'bg-[hsl(var(--presence-online))] text-white border-0' : ''}>{u.is_active !== false ? 'Active' : 'Inactive'}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Switch checked={u.is_active !== false} onCheckedChange={() => handleStatusToggle(u.user_id, u.is_active !== false)} disabled={u.user_id === adminUser?.user_id} data-testid={`admin-status-toggle-${u.user_id}`} />
                            {u.user_id !== adminUser?.user_id && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => handleDeleteUser(u.user_id, u.email)} data-testid={`admin-delete-user-${u.user_id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </Card>
          </TabsContent>

          {/* ================== CHANNELS TAB ================== */}
          <TabsContent value="channels">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <Card><CardContent className="pt-4 pb-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary)/0.10)] flex items-center justify-center"><Hash className="w-5 h-5 text-[hsl(var(--primary))]" /></div>
                <div><p className="text-2xl font-bold" style={{fontFamily: 'Space Grotesk'}}>{adminChannels.length}</p><p className="text-xs text-muted-foreground">Total Channels</p></div>
              </CardContent></Card>
              <Card><CardContent className="pt-4 pb-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary)/0.10)] flex items-center justify-center"><Lock className="w-5 h-5 text-[hsl(var(--primary))]" /></div>
                <div><p className="text-2xl font-bold" style={{fontFamily: 'Space Grotesk'}}>{adminChannels.filter(c => c.is_private).length}</p><p className="text-xs text-muted-foreground">Private Channels</p></div>
              </CardContent></Card>
              <Card><CardContent className="pt-4 pb-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary)/0.10)] flex items-center justify-center"><Users className="w-5 h-5 text-[hsl(var(--primary))]" /></div>
                <div><p className="text-2xl font-bold" style={{fontFamily: 'Space Grotesk'}}>{adminTeams.length}</p><p className="text-xs text-muted-foreground">Teams</p></div>
              </CardContent></Card>
            </div>

            {/* Teams section */}
            <Card className="mb-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-base" style={{fontFamily: 'Space Grotesk'}}>Teams</CardTitle>
              </CardHeader>
              <CardContent>
                {adminTeams.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No teams created.</p>
                ) : (
                  <div className="space-y-2">
                    {adminTeams.map(t => (
                      <div key={t.team_id} className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--surface-2))]" data-testid={`admin-team-${t.team_id}`}>
                        <div>
                          <p className="text-sm font-medium">{t.name}</p>
                          <p className="text-xs text-muted-foreground">{t.member_count} members &middot; {t.channel_count} channels</p>
                        </div>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => handleDeleteTeam(t.team_id, t.name)} data-testid={`admin-delete-team-${t.team_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Channels table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base" style={{fontFamily: 'Space Grotesk'}}>All Channels</CardTitle>
              </CardHeader>
              <ScrollArea className="max-h-[50vh]">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Channel</TableHead><TableHead>Team</TableHead><TableHead>Members</TableHead><TableHead>Messages</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {adminChannels.map(ch => (
                      <TableRow key={ch.channel_id} data-testid={`admin-channel-row-${ch.channel_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {ch.is_private ? <Lock className="w-3.5 h-3.5 text-muted-foreground" /> : <Hash className="w-3.5 h-3.5 text-muted-foreground" />}
                            <span className="text-sm font-medium">{ch.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{ch.team_name}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{ch.member_count}</Badge></TableCell>
                        <TableCell className="text-sm text-muted-foreground">{ch.message_count}</TableCell>
                        <TableCell><Badge variant={ch.is_private ? 'secondary' : 'default'} className={ch.is_private ? '' : 'bg-[hsl(var(--presence-online))] text-white border-0'}>{ch.is_private ? 'Private' : 'Public'}</Badge></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => handleDeleteChannel(ch.channel_id, ch.name)} data-testid={`admin-delete-channel-${ch.channel_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {adminChannels.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No channels found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </Card>
          </TabsContent>

          {/* ================== APP CONTROL TAB ================== */}
          <TabsContent value="app-control">
            <div className="space-y-6" data-testid="app-control-section">

              {/* Maintenance Mode */}
              <Card data-testid="maintenance-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Wrench className="w-5 h-5 text-[hsl(var(--primary))]" />
                      <CardTitle className="text-base" style={{fontFamily: 'Space Grotesk'}}>Maintenance Mode</CardTitle>
                    </div>
                    <Switch checked={maintenance.enabled} onCheckedChange={toggleMaintenance} data-testid="maintenance-toggle" />
                  </div>
                  <CardDescription>When enabled, users see a maintenance screen instead of the app.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Maintenance message for users..."
                      value={maintenance.message}
                      onChange={(e) => setMaintenance(p => ({ ...p, message: e.target.value }))}
                      data-testid="maintenance-message-input"
                    />
                    <Button variant="outline" size="sm" onClick={saveMaintMessage} data-testid="maintenance-save-btn">Save</Button>
                  </div>
                </CardContent>
              </Card>

              {/* Version Policy */}
              <Card data-testid="version-policy-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 text-[hsl(var(--primary))]" />
                      <CardTitle className="text-base" style={{fontFamily: 'Space Grotesk'}}>Version Policy</CardTitle>
                    </div>
                    <Button size="sm" onClick={() => setShowVersionDialog(true)} data-testid="version-policy-add-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Set Policy</Button>
                  </div>
                  <CardDescription>Set minimum app version requirements and force-update rules per platform.</CardDescription>
                </CardHeader>
                <CardContent>
                  {versionPolicies.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No version policies configured.</p>
                  ) : (
                    <div className="space-y-2">
                      {versionPolicies.map((vp, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--surface-2))]">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="capitalize">{vp.platform}</Badge>
                            <span className="text-sm">Min version: <strong>{vp.min_version}</strong></span>
                          </div>
                          <div className="flex items-center gap-2">
                            {vp.force_update && <Badge className="bg-red-100 text-red-700 border-red-200">Force Update</Badge>}
                            {vp.store_url && <span className="text-xs text-muted-foreground truncate max-w-[200px]">{vp.store_url}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Feature Flags */}
              <Card data-testid="feature-flags-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Flag className="w-5 h-5 text-[hsl(var(--primary))]" />
                      <CardTitle className="text-base" style={{fontFamily: 'Space Grotesk'}}>Feature Flags</CardTitle>
                    </div>
                    <Button size="sm" onClick={() => setShowFlagDialog(true)} data-testid="flag-add-btn"><Plus className="w-3.5 h-3.5 mr-1" /> New Flag</Button>
                  </div>
                  <CardDescription>Toggle features on/off for all users remotely.</CardDescription>
                </CardHeader>
                <CardContent>
                  {flags.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No feature flags configured.</p>
                  ) : (
                    <div className="space-y-2">
                      {flags.map(f => (
                        <div key={f.key} className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--surface-2))]" data-testid={`flag-row-${f.key}`}>
                          <div>
                            <p className="text-sm font-medium">{f.label || f.key}</p>
                            <p className="text-xs text-muted-foreground font-mono">{f.key}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch checked={f.enabled} onCheckedChange={() => toggleFlag(f.key, f.enabled)} data-testid={`flag-toggle-${f.key}`} />
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteFlag(f.key)} data-testid={`flag-delete-${f.key}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Banners */}
              <Card data-testid="banners-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-[hsl(var(--primary))]" />
                      <CardTitle className="text-base" style={{fontFamily: 'Space Grotesk'}}>Banners</CardTitle>
                    </div>
                    <Button size="sm" onClick={() => setShowBannerDialog(true)} data-testid="banner-add-btn"><Plus className="w-3.5 h-3.5 mr-1" /> New Banner</Button>
                  </div>
                  <CardDescription>Display global banners to all app users (info, warning, critical).</CardDescription>
                </CardHeader>
                <CardContent>
                  {banners.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No banners created.</p>
                  ) : (
                    <div className="space-y-2">
                      {banners.map(b => (
                        <div key={b.banner_id} className={`flex items-center justify-between p-3 rounded-lg border ${levelColors[b.level] || 'bg-muted'}`} data-testid={`banner-row-${b.banner_id}`}>
                          <div>
                            <p className="text-sm font-semibold">{b.title}</p>
                            <p className="text-xs opacity-80">{b.message}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge variant="outline" className="capitalize text-xs">{b.level}</Badge>
                            <Switch checked={b.active} onCheckedChange={() => toggleBanner(b.banner_id, b.active)} />
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteBanner(b.banner_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Announcements */}
              <Card data-testid="announcements-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Megaphone className="w-5 h-5 text-[hsl(var(--primary))]" />
                      <CardTitle className="text-base" style={{fontFamily: 'Space Grotesk'}}>Announcements</CardTitle>
                    </div>
                    <Button size="sm" onClick={() => setShowAnnouncementDialog(true)} data-testid="announcement-add-btn"><Plus className="w-3.5 h-3.5 mr-1" /> New</Button>
                  </div>
                  <CardDescription>Post announcements shown to all users on app load.</CardDescription>
                </CardHeader>
                <CardContent>
                  {announcements.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No announcements.</p>
                  ) : (
                    <div className="space-y-2">
                      {announcements.map(a => (
                        <div key={a.announcement_id} className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--surface-2))]">
                          <div>
                            <p className="text-sm font-medium">{a.title}</p>
                            <p className="text-xs text-muted-foreground line-clamp-1">{a.body}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Switch checked={a.active} onCheckedChange={() => toggleAnnouncement(a.announcement_id, a.active)} />
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteAnnouncement(a.announcement_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Push Notifications */}
              <Card data-testid="notifications-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bell className="w-5 h-5 text-[hsl(var(--primary))]" />
                      <CardTitle className="text-base" style={{fontFamily: 'Space Grotesk'}}>Push Notifications</CardTitle>
                    </div>
                    <Button size="sm" onClick={() => setShowNotifDialog(true)} data-testid="notif-send-btn"><Send className="w-3.5 h-3.5 mr-1" /> Send</Button>
                  </div>
                  <CardDescription>Send push notifications to mobile app users. Registered devices: <strong>{devices.length}</strong></CardDescription>
                </CardHeader>
                <CardContent>
                  {notifications.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No notifications sent yet.</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {notifications.slice(0, 10).map(n => (
                        <div key={n.notif_id} className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--surface-2))] text-sm">
                          <div>
                            <p className="font-medium">{n.title}</p>
                            <p className="text-xs text-muted-foreground">{n.body}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <Badge variant="outline" className="text-xs capitalize">{n.status}</Badge>
                            <p className="text-[10px] text-muted-foreground mt-0.5">Target: {n.target}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* App Settings */}
              <Card data-testid="app-settings-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Settings className="w-5 h-5 text-[hsl(var(--primary))]" />
                      <CardTitle className="text-base" style={{fontFamily: 'Space Grotesk'}}>App Settings</CardTitle>
                    </div>
                    <Button size="sm" onClick={() => setShowSettingDialog(true)} data-testid="setting-add-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                  </div>
                  <CardDescription>Remote key-value settings for app behavior (max attachment size, disable calls, etc.).</CardDescription>
                </CardHeader>
                <CardContent>
                  {appSettings.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No custom settings.</p>
                  ) : (
                    <div className="space-y-2">
                      {appSettings.map(s => (
                        <div key={s.key} className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--surface-2))]">
                          <div><span className="text-sm font-mono font-medium">{s.key}</span> = <span className="text-sm text-muted-foreground">{s.value}</span></div>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteSetting(s.key)}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ================== DIALOGS ================== */}

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="admin-create-user-dialog">
          <DialogHeader><DialogTitle style={{fontFamily: 'Space Grotesk'}}>Create New User</DialogTitle><DialogDescription>Add a new team member to Tello.</DialogDescription></DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="space-y-2"><Label>Username</Label><Input value={newUser.username} onChange={(e) => setNewUser({...newUser, username: e.target.value})} required data-testid="admin-new-username" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required data-testid="admin-new-email" /></div>
            <div className="space-y-2"><Label>Password</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required data-testid="admin-new-password" /></div>
            <div className="space-y-2"><Label>Display Name</Label><Input value={newUser.display_name} onChange={(e) => setNewUser({...newUser, display_name: e.target.value})} data-testid="admin-new-display-name" /></div>
            <div className="space-y-2"><Label>Role</Label>
              <Select value={newUser.system_role} onValueChange={(val) => setNewUser({...newUser, system_role: val})}>
                <SelectTrigger data-testid="admin-new-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="member">Member</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} data-testid="admin-create-submit">{creating ? 'Creating...' : 'Create User'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Feature Flag Dialog */}
      <Dialog open={showFlagDialog} onOpenChange={setShowFlagDialog}>
        <DialogContent data-testid="flag-dialog">
          <DialogHeader><DialogTitle style={{fontFamily: 'Space Grotesk'}}>Create Feature Flag</DialogTitle></DialogHeader>
          <form onSubmit={createFlag} className="space-y-4">
            <div className="space-y-2"><Label>Key (unique identifier)</Label><Input placeholder="e.g. dark_mode" value={flagForm.key} onChange={(e) => setFlagForm({...flagForm, key: e.target.value.toLowerCase().replace(/\s+/g, '_')})} required data-testid="flag-key-input" /></div>
            <div className="space-y-2"><Label>Label</Label><Input placeholder="Display name" value={flagForm.label} onChange={(e) => setFlagForm({...flagForm, label: e.target.value})} data-testid="flag-label-input" /></div>
            <div className="flex items-center gap-2"><Switch checked={flagForm.enabled} onCheckedChange={(v) => setFlagForm({...flagForm, enabled: v})} /><Label>Enabled by default</Label></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setShowFlagDialog(false)}>Cancel</Button><Button type="submit" data-testid="flag-create-submit">Create Flag</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Banner Dialog */}
      <Dialog open={showBannerDialog} onOpenChange={setShowBannerDialog}>
        <DialogContent data-testid="banner-dialog">
          <DialogHeader><DialogTitle style={{fontFamily: 'Space Grotesk'}}>Create Banner</DialogTitle></DialogHeader>
          <form onSubmit={createBanner} className="space-y-4">
            <div className="space-y-2"><Label>Title</Label><Input value={bannerForm.title} onChange={(e) => setBannerForm({...bannerForm, title: e.target.value})} required data-testid="banner-title-input" /></div>
            <div className="space-y-2"><Label>Message</Label><Textarea value={bannerForm.message} onChange={(e) => setBannerForm({...bannerForm, message: e.target.value})} required data-testid="banner-message-input" /></div>
            <div className="space-y-2"><Label>Level</Label>
              <Select value={bannerForm.level} onValueChange={(v) => setBannerForm({...bannerForm, level: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="info">Info</SelectItem><SelectItem value="warning">Warning</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent>
              </Select>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setShowBannerDialog(false)}>Cancel</Button><Button type="submit" data-testid="banner-create-submit">Create Banner</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Announcement Dialog */}
      <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
        <DialogContent data-testid="announcement-dialog">
          <DialogHeader><DialogTitle style={{fontFamily: 'Space Grotesk'}}>Create Announcement</DialogTitle></DialogHeader>
          <form onSubmit={createAnnouncement} className="space-y-4">
            <div className="space-y-2"><Label>Title</Label><Input value={announcementForm.title} onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})} required data-testid="announcement-title-input" /></div>
            <div className="space-y-2"><Label>Body</Label><Textarea value={announcementForm.body} onChange={(e) => setAnnouncementForm({...announcementForm, body: e.target.value})} required data-testid="announcement-body-input" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setShowAnnouncementDialog(false)}>Cancel</Button><Button type="submit" data-testid="announcement-create-submit">Create</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Push Notification Dialog */}
      <Dialog open={showNotifDialog} onOpenChange={setShowNotifDialog}>
        <DialogContent data-testid="notif-dialog">
          <DialogHeader><DialogTitle style={{fontFamily: 'Space Grotesk'}}>Send Push Notification</DialogTitle><DialogDescription>Actual delivery requires FCM/APNs configuration.</DialogDescription></DialogHeader>
          <form onSubmit={sendNotification} className="space-y-4">
            <div className="space-y-2"><Label>Target</Label>
              <Select value={notifForm.target} onValueChange={(v) => setNotifForm({...notifForm, target: v})}>
                <SelectTrigger data-testid="notif-target-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Users</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Title</Label><Input value={notifForm.title} onChange={(e) => setNotifForm({...notifForm, title: e.target.value})} required data-testid="notif-title-input" /></div>
            <div className="space-y-2"><Label>Body</Label><Textarea value={notifForm.body} onChange={(e) => setNotifForm({...notifForm, body: e.target.value})} required data-testid="notif-body-input" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setShowNotifDialog(false)}>Cancel</Button><Button type="submit" data-testid="notif-send-submit"><Send className="w-4 h-4 mr-1" /> Send</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* App Setting Dialog */}
      <Dialog open={showSettingDialog} onOpenChange={setShowSettingDialog}>
        <DialogContent data-testid="setting-dialog">
          <DialogHeader><DialogTitle style={{fontFamily: 'Space Grotesk'}}>Add App Setting</DialogTitle></DialogHeader>
          <form onSubmit={saveSetting} className="space-y-4">
            <div className="space-y-2"><Label>Key</Label><Input placeholder="e.g. max_attachment_size_mb" value={settingForm.key} onChange={(e) => setSettingForm({...settingForm, key: e.target.value})} required data-testid="setting-key-input" /></div>
            <div className="space-y-2"><Label>Value</Label><Input placeholder="e.g. 25" value={settingForm.value} onChange={(e) => setSettingForm({...settingForm, value: e.target.value})} required data-testid="setting-value-input" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setShowSettingDialog(false)}>Cancel</Button><Button type="submit" data-testid="setting-save-submit">Save Setting</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Version Policy Dialog */}
      <Dialog open={showVersionDialog} onOpenChange={setShowVersionDialog}>
        <DialogContent data-testid="version-dialog">
          <DialogHeader><DialogTitle style={{fontFamily: 'Space Grotesk'}}>Set Version Policy</DialogTitle></DialogHeader>
          <form onSubmit={saveVersionPolicy} className="space-y-4">
            <div className="space-y-2"><Label>Platform</Label>
              <Select value={versionForm.platform} onValueChange={(v) => setVersionForm({...versionForm, platform: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="android">Android</SelectItem><SelectItem value="ios">iOS</SelectItem><SelectItem value="all">All Platforms</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Minimum Version</Label><Input placeholder="1.0.0" value={versionForm.min_version} onChange={(e) => setVersionForm({...versionForm, min_version: e.target.value})} required data-testid="version-min-input" /></div>
            <div className="flex items-center gap-2"><Switch checked={versionForm.force_update} onCheckedChange={(v) => setVersionForm({...versionForm, force_update: v})} /><Label>Force Update (block app until updated)</Label></div>
            <div className="space-y-2"><Label>Store URL (optional)</Label><Input placeholder="https://play.google.com/store/apps/..." value={versionForm.store_url} onChange={(e) => setVersionForm({...versionForm, store_url: e.target.value})} data-testid="version-store-input" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setShowVersionDialog(false)}>Cancel</Button><Button type="submit" data-testid="version-save-submit">Save Policy</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
